import { useEffect, useRef, useState } from 'react'
import { sb, API_URL } from '../utils/config'
import { studioEvents } from '../utils/metaPixel'
import { VideoAnalysisResult, FondateurBanner, FondateurFooter } from '../pages/StudioAnalysisTab'
import merlinSort from '../assets/Merlin_sort-removebg-preview.png'
import merlinIdee from '../assets/Merlin_idée_1-removebg-preview.png'
import merlinRecherche from '../assets/Merlin_recherche_boule_de_cristal-removebg-preview.png'

// ═══════════════════════════════════════════════════════════════
// ANALYSE VIDÉO AUTO POST-SCAN (05/08, A/B note_va_auto)
// Carte compacte AU-DESSUS des 3 formats : la miniature de SA vidéo en fond,
// le début de l'analyse en transparence par-dessus, tronqué en fondu pour
// inciter à cliquer. Clic : gaté → paywall Pro / débloqué → rapport complet
// en modal (le rendu habituel, même composant que l'onglet Analyse).
// Le gating vit côté serveur : le gaté ne reçoit que le teaser.
// ═══════════════════════════════════════════════════════════════

function fmtViews(n) {
  if (!n) return null
  if (n >= 1e6) return `${(n / 1e6).toFixed(1).replace('.0', '')}M de vues`
  if (n >= 1e3) return `${Math.round(n / 1e3)}k vues`
  return `${n} vues`
}

// Rapport complet en modal : le rendu habituel des analyses (7 dimensions,
// commentaires vs niche, blocs profonds), scrollable, fond crème de l'app.
function VaModal({ data, onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])
  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(10,8,30,0.6)' }} onClick={onClose}>
      <div className="bg-bg w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl"
        onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-bg border-b border-border">
          <p className="text-[15px] font-bold text-ink">🎬 L'analyse de ta dernière vidéo</p>
          <button type="button" onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-ink-muted hover:text-ink hover:bg-ink/5">✕</button>
        </div>
        <div className="p-4">
          <VideoAnalysisResult data={data} plan="pro_plus" hideHeader hideUpgrade onClose={null} />
        </div>
      </div>
    </div>
  )
}

export default function AutoVaCard({ sessionId, isGuest = false, className = '', previewReel = null }) {
  const [data, setData] = useState(null)
  const [open, setOpen] = useState(false)
  const [started, setStarted] = useState(false)
  const polls = useRef(0)
  const tracked = useRef(false)
  const previewShownTracked = useRef(false)
  const loadRef = useRef(null)

  // Overrides dev/screenshots : ?vad=<sessionId> force la session,
  // ?vatok=<jwt> force le bearer (localhost uniquement).
  const params = (() => { try { return new URLSearchParams(window.location.search) } catch { return new URLSearchParams() } })()
  const sid = params.get('vad') || sessionId

  useEffect(() => {
    if (!sid) return
    let stop = false
    async function load() {
      try {
        const headers = {}
        try {
          const { data: s } = await sb.auth.getSession()
          if (s?.session?.access_token) headers.Authorization = `Bearer ${s.session.access_token}`
        } catch { /* invité */ }
        try {
          const tok = params.get('vatok')
          if (tok && window.location.hostname === 'localhost') headers.Authorization = `Bearer ${tok}`
        } catch { /* no-op */ }
        const r = await fetch(`${API_URL}/api/studio/auto-va/${sid}`, { headers })
        const j = await r.json()
        if (stop) return
        setData(j)
        // repoll tant que l'analyse tourne (l'analyse arrive 1-2 min après le scan)
        if (j?.available && j.status !== 'done' && polls.current < 24) {
          polls.current += 1
          setTimeout(load, 10000)
        }
        // available:false = la ligne n'existe PAS ENCORE : l'analyse auto est
        // déclenchée à la FIN du scan, or les résultats s'affichent dès la
        // note (~11s). Sans repoll ici, la carte disparaissait pour toujours
        // (« pas de barre de chargement », retour Antonin 11/08). On repoll
        // ~3 min ; toujours rien après (pas de reel analysable, réutilisation
        // impossible) → la carte s'efface proprement.
        if (!j?.available && polls.current < 18) {
          polls.current += 1
          setTimeout(load, 10000)
        }
        if (j?.status === 'done' && !tracked.current) {
          tracked.current = true
          try { studioEvents.tabClicked?.({ tab: 'auto_va_shown', source_page: 'results', entitled: !!j.entitled }) } catch { /* no-op */ }
        }
      } catch { /* silencieux : la carte ne doit jamais casser les résultats */ }
    }
    loadRef.current = load
    load()
    return () => { stop = true }
  }, [sid])

  // Déclenchement AU CLIC (11/08) : l'analyse ne tourne plus au scan pour les
  // invités. Fire-and-forget + relance du polling (l'état « en cours »
  // s'affiche dès que le serveur crée la ligne).
  function startAnalysis() {
    if (started) return
    setStarted(true)
    try { studioEvents.tabClicked?.({ tab: 'auto_va_start_clicked', source_page: 'results', guest: isGuest }) } catch { /* no-op */ }
    try {
      let guestId = ''
      try { guestId = localStorage.getItem('studio_anon_id') || '' } catch { /* no-op */ }
      fetch(`${API_URL}/api/studio/auto-va/${sid}/start?guest_id=${encodeURIComponent(guestId)}`, { method: 'POST' }).catch(() => {})
    } catch { /* no-op */ }
    polls.current = 0
    setTimeout(() => { try { loadRef.current?.() } catch { /* no-op */ } }, 4000)
  }

  // Pas de ligne d'analyse : soit l'APERÇU immédiat (miniature du scan,
  // l'analyse part au clic — décision Antonin 11/08), soit l'état « en
  // cours » si le clic est parti, soit rien après épuisement du polling.
  const waitingCreation = !data?.available && (started || !previewReel) && polls.current < 18
  const showPreview = !data?.available && !started && !!(previewReel && (previewReel.thumbnail || previewReel.shortcode))
  // Impression de la carte APERÇU : dénominateur du CTR de
  // auto_va_start_clicked (une fois par affichage de session).
  if (showPreview && !previewShownTracked.current) {
    previewShownTracked.current = true
    try { studioEvents.tabClicked?.({ tab: 'auto_va_preview_shown', source_page: 'results', guest: isGuest }) } catch { /* no-op */ }
  }
  if (!data?.available && !waitingCreation && !showPreview) return null

  if (showPreview) {
    const pMeta = [previewReel.vues ? fmtViews(previewReel.vues) : null, 'ta vidéo la plus récente'].filter(Boolean).join(' · ')
    const onPreviewClick = () => { startAnalysis(); unlock() }
    return (
      <div role="button" tabIndex={0} onClick={onPreviewClick}
        onKeyDown={e => { if (e.key === 'Enter') onPreviewClick() }}
        className={`rounded-2xl border overflow-hidden relative cursor-pointer ${className}`}
        style={{ borderColor: 'rgba(254,130,4,0.45)', background: '#0f1040' }}>
        <div className="relative" style={{ height: 330 }}>
          {previewReel.thumbnail
            ? <img src={previewReel.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none' }} />
            : <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-accent, #fe8204))' }} />}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,8,30,0.55) 0%, rgba(10,8,30,0.15) 30%, rgba(10,8,30,0.55) 55%, rgba(10,8,30,0.95) 100%)' }} />
          <div className="absolute top-3 left-3 right-3">
            <p className="text-[15px] font-bold text-white leading-tight" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>🎬 J'ai analysé ta dernière vidéo</p>
            <p className="text-[11.5px] mt-0.5" style={{ color: 'rgba(255,255,255,0.75)', textShadow: '0 1px 6px rgba(0,0,0,0.5)' }}>{pMeta}</p>
          </div>
          <div className="absolute inset-x-0 bottom-0 px-3 pb-3">
            <div className="rounded-xl px-3 pt-2.5 pb-2.5"
              style={{ background: 'rgba(20,15,45,0.55)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.14)' }}>
              <div className="text-[10px] font-extrabold uppercase tracking-wider mb-1.5" style={{ color: '#fdba74' }}>Pour t'améliorer</div>
              {/* Le contenu est verrouillé : lignes floutées, jamais de faux texte */}
              <div aria-hidden="true">
                {[92, 78, 55].map((w, i) => (
                  <div key={i} className="rounded mb-1.5" style={{ height: 9, width: `${w}%`, background: 'rgba(255,255,255,0.35)', filter: 'blur(3px)' }} />
                ))}
              </div>
            </div>
            <button type="button"
              onClick={e => { e.stopPropagation(); onPreviewClick() }}
              className="w-full py-2.5 rounded-xl text-white font-bold text-[13.5px] mt-2"
              style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 6px 16px rgba(254,130,4,0.35)' }}>
              🔒 Débloquer mon analyse complète
            </button>
            <p className="text-[10.5px] text-center mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Les 7 notes, les corrections et le comparatif de ta niche t'attendent</p>
          </div>
        </div>
      </div>
    )
  }

  function unlock() {
    try { studioEvents.tabClicked?.({ tab: 'auto_va_unlock_clicked', source_page: 'results', guest: isGuest }) } catch { /* no-op */ }
    try {
      if (isGuest) {
        window.dispatchEvent(new CustomEvent('studio:open-guest-signup', { detail: { trigger: 'auto_va' } }))
      } else {
        window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', { detail: { trigger: 'auto_va', upgrade_to: 'pro_plus' } }))
      }
    } catch { /* no-op */ }
  }

  const t = data?.teaser || {}
  const meta = [t.play_count ? fmtViews(t.play_count) : null, 'ta vidéo la plus récente'].filter(Boolean).join(' · ')

  // ── ANALYSE EN COURS (ou pas encore créée) : mascotte + spinner ──
  if (waitingCreation || data?.status !== 'done') {
    const pct = data?.progress_pct || 0
    const merlinImg = pct > 70 ? merlinIdee : pct > 30 ? merlinSort : merlinRecherche
    return (
      <div className={`rounded-2xl border p-4 ${className}`} style={{ borderColor: 'rgba(254,130,4,0.4)', background: '#fff' }}>
        <div className="flex items-center gap-3.5">
          <img src={merlinImg} alt="Merlin" className="w-16 h-16 object-contain shrink-0"
            style={{ filter: 'drop-shadow(0 4px 12px rgba(254,130,4,0.30))' }} />
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-bold text-ink leading-tight">Analyse de ta dernière vidéo en cours</p>
            <p className="text-[12px] text-ink-muted">{data?.current_step || 'Image par image, seconde par seconde. Prête dans 1 à 2 minutes, juste ici.'}</p>
          </div>
          <div className="ml-auto shrink-0 w-5 h-5 rounded-full border-2 animate-spin"
            style={{ borderColor: 'var(--merlin-accent, #fe8204)', borderTopColor: 'transparent' }} />
        </div>
      </div>
    )
  }

  const full = data.full
  // Le début de l'analyse en clair : l'insight de l'axe le plus faible, et si
  // on a le rapport complet, on enchaîne d'autres détails pour que le fondu
  // coupe au milieu d'une vraie phrase.
  const a = full?.scoring?.analyse_json || {}
  const previewParts = [t.insight]
  if (full) {
    for (const k of ['hook', 'boucles', 'densite', 'langage', 'rehooks', 'connexion']) {
      const d = a[k]?.detail
      if (d && d !== t.insight) previewParts.push(d)
      if (previewParts.join(' ').length > 300) break
    }
  }
  const preview = previewParts.filter(Boolean).join(' ')

  function onCardClick() {
    if (full) {
      try { studioEvents.tabClicked?.({ tab: 'auto_va_opened', source_page: 'results' }) } catch { /* no-op */ }
      setOpen(true)
    } else {
      unlock()
    }
  }

  return (
    <>
      <div role="button" tabIndex={0} onClick={onCardClick}
        onKeyDown={e => { if (e.key === 'Enter') onCardClick() }}
        className={`rounded-2xl border overflow-hidden relative cursor-pointer ${className}`}
        style={{ borderColor: 'rgba(254,130,4,0.45)', background: '#0f1040' }}>
        <div className="relative" style={{ height: 330 }}>
          {/* La miniature de SA vidéo en fond */}
          {t.thumbnail
            ? <img src={t.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover" />
            : <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-accent, #fe8204))' }} />}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,8,30,0.55) 0%, rgba(10,8,30,0.15) 30%, rgba(10,8,30,0.55) 55%, rgba(10,8,30,0.95) 100%)' }} />

          {/* En-tête : la preuve que c'est SA vidéo + la note */}
          <div className="absolute top-3 left-3 right-3 flex items-start gap-2">
            <div className="min-w-0">
              <p className="text-[15px] font-bold text-white leading-tight" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}>🎬 J'ai analysé ta dernière vidéo</p>
              <p className="text-[11.5px] mt-0.5" style={{ color: 'rgba(255,255,255,0.75)', textShadow: '0 1px 6px rgba(0,0,0,0.5)' }}>{meta}</p>
            </div>
            {typeof t.note_globale === 'number' && (
              <div className="ml-auto shrink-0 text-center">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-extrabold text-[14px] text-white"
                  style={{ background: t.note_globale >= 7 ? '#16a34a' : t.note_globale >= 5 ? 'var(--merlin-accent, #fe8204)' : '#dc2626', boxShadow: '0 2px 10px rgba(0,0,0,0.4)' }}>
                  {t.note_globale}
                </div>
                <div className="text-[9px] mt-0.5" style={{ color: 'rgba(255,255,255,0.7)' }}>/10</div>
              </div>
            )}
          </div>

          {/* Le début de l'analyse en transparence, tronqué en fondu */}
          <div className="absolute inset-x-0 bottom-0 px-3 pb-3">
            <div className="rounded-xl px-3 pt-2.5 pb-1"
              style={{ background: 'rgba(20,15,45,0.55)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.14)' }}>
              <div className="text-[10px] font-extrabold uppercase tracking-wider mb-1" style={{ color: '#fdba74' }}>Pour t'améliorer</div>
              <div style={{
                maxHeight: 66, overflow: 'hidden',
                WebkitMaskImage: 'linear-gradient(180deg, #000 30%, transparent 100%)',
                maskImage: 'linear-gradient(180deg, #000 30%, transparent 100%)',
              }}>
                <p className="text-[13px] leading-snug" style={{ color: 'rgba(255,255,255,0.94)' }}>{preview}</p>
              </div>
            </div>
            <button type="button"
              onClick={e => { e.stopPropagation(); onCardClick() }}
              className="w-full py-2.5 rounded-xl text-white font-bold text-[13.5px] mt-2"
              style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 6px 16px rgba(254,130,4,0.35)' }}>
              {full ? 'Lire mon analyse complète' : '🔒 Débloquer mon analyse complète'}
            </button>
            {!full && (
              <p className="text-[10.5px] text-center mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Les 7 notes, les corrections et le comparatif de ta niche t'attendent</p>
            )}
          </div>
        </div>
      </div>

      {open && full && <VaModal data={full} onClose={() => setOpen(false)} />}
    </>
  )
}


// ── PREVIEW DEV (localhost) : rendu réel de la carte pour validation ──
export function VaPreviewPage() {
  const q = new URLSearchParams(window.location.search)
  if (q.get('fbanner') === '1') {
    return (
      <div className="min-h-screen px-3.5 py-5" style={{ background: '#f4f2fa' }}>
        <div className="max-w-md mx-auto">
          <span className="text-[10.5px] font-extrabold px-2.5 py-1 rounded-full tracking-wide inline-block mb-3"
            style={{ background: 'rgba(254,130,4,.12)', border: '1px solid rgba(254,130,4,.45)', color: '#d97206' }}>
            🎁 ANALYSE OFFERTE · TOUT EST DÉBLOQUÉ
          </span>
          <div className="rounded-2xl bg-white border border-border p-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="rounded mb-2.5" style={{ height: 12, width: `${92 - i * 9}%`, background: 'rgba(15,16,64,0.08)' }} />
            ))}
          </div>
          <FondateurFooter onCta={() => {}} />
        </div>
      </div>
    )
  }
  return (
    <div className="min-h-screen bg-bg px-4 py-6">
      <div className="max-w-md mx-auto">
        <h1 className="text-xl font-bold text-ink mb-1">Ton analyse de <span style={{ color: 'var(--merlin-accent, #fe8204)' }}>@tibogoodvibes</span> est prête ✨</h1>
        <p className="text-[13px] text-ink-muted mb-4">(aperçu dev : note du compte ici, puis la carte, puis les 3 formats)</p>
        <AutoVaCard sessionId={q.get('vad')} isGuest={!q.get('vatok')} className="mb-4" />
        <div className="rounded-2xl border border-border bg-white p-4">
          <p className="text-[14px] font-bold text-ink mb-2">Tes 3 formats viraux adaptés</p>
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3 rounded-xl p-3 mb-2" style={{ background: 'rgba(15,16,64,0.04)' }}>
              <div className="w-9 h-12 rounded-lg" style={{ background: 'rgba(15,16,64,0.12)' }} />
              <div className="flex-1">
                <div className="rounded mb-1.5" style={{ height: 10, width: '70%', background: 'rgba(15,16,64,0.14)' }} />
                <div className="rounded" style={{ height: 8, width: '45%', background: 'rgba(15,16,64,0.10)' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
